/**
 * Resource runtime adapter registry.
 *
 * Static and authorized descriptor structure is backend-owned. This registry
 * contains React query hooks and response selectors only; it never decides
 * columns, fields, actions, routes, or permissions.
 */
import { INSTRUMENTS_HOOKS, useInstrumentList, useInstrumentGet } from './resources/instruments/index.js';
import { CORPORATE_ACTIONS_HOOKS, useCorporateActionList, useCorporateActionGet } from './resources/corporate-actions/index.js';
import { FACT_BATCHES_HOOKS, useFactBatchList, useFactBatchGet } from './resources/fact-batches/index.js';
import { RECONCILIATION_RUNS_HOOKS, useReconciliationRunList, useReconciliationRunGet } from './resources/reconciliation-runs/index.js';
import { IMPORT_JOBS_HOOKS, useImportList, useImportGet } from './resources/import-jobs/index.js';
import { ORGANIZATION_USERS_HOOKS } from './resources/organization-users/index.js';
import { ORGANIZATION_ROLES_HOOKS } from './resources/organization-roles/index.js';
import {
  PLATFORM_ORGANIZATIONS_HOOKS,
  ORGANIZATION_BRANCHES_HOOKS,
  ORGANIZATION_CLIENTS_HOOKS,
  ORGANIZATION_CLIENT_ACCOUNTS_HOOKS,
  ORGANIZATION_PORTFOLIOS_HOOKS,
  ORGANIZATION_AUDIT_HOOKS,
  ORGANIZATION_MODULE_ACTIVATIONS_HOOKS,
} from './resources/admin-hooks.js';
import {
  DATA_CLIENTS_HOOKS,
  DATA_PORTFOLIOS_HOOKS,
  DATA_POSITIONS_HOOKS,
  DATA_JOURNALS_HOOKS,
  DATA_FISCAL_PERIODS_HOOKS,
  DATA_TAX_RECORDS_HOOKS,
  DATA_RISK_EXPOSURES_HOOKS,
} from './resources/data-hub-hooks.js';
import {
  OPERATION_IDS,
  ROUTE_IDS,
  type OperationId,
  type RouteId,
} from '@finance/contracts';

export interface ListHookResult<T = unknown> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

export interface GetHookResult<T = unknown> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

export interface ResourceHookBinding<TList = unknown, TDetail = unknown> {
  useList?: () => ListHookResult<TList>;
  selectList?: (response: unknown) => TList;
  useGet?: (id: string) => GetHookResult<TDetail>;
  selectDetail?: (response: unknown) => TDetail;
  rowIdExtractor?: (row: Record<string, unknown>) => string | undefined;
  rowIdField: string;
  routeParameterName: string;
  /** @deprecated Use routeParameterName. */
  paramName: string;
  listRouteId: RouteId;
  detailRouteId: RouteId;
  readOperationId: OperationId;
  detailDescriptorId?: string;
}

export { useInstrumentList, useInstrumentGet } from './resources/instruments/index.js';
export { useCorporateActionList, useCorporateActionGet } from './resources/corporate-actions/index.js';
export { useFactBatchList, useFactBatchGet } from './resources/fact-batches/index.js';
export { useReconciliationRunList, useReconciliationRunGet } from './resources/reconciliation-runs/index.js';
export { useImportList, useImportGet } from './resources/import-jobs/index.js';

const PLATFORM_USERS_ROUTE_BINDING: ResourceHookBinding = {
  rowIdField: 'id',
  routeParameterName: 'userId',
  paramName: 'userId',
  listRouteId: ROUTE_IDS.PLATFORM_USERS_LIST,
  detailRouteId: ROUTE_IDS.PLATFORM_USERS_DETAIL,
  readOperationId: OPERATION_IDS.PLATFORM_USERS_READ,
};

const FEE_RULES_HOOKS: ResourceHookBinding = {
  rowIdField: 'id',
  routeParameterName: 'ruleId',
  paramName: 'ruleId',
  listRouteId: ROUTE_IDS.ADMIN_FEE_RULES_LIST,
  detailRouteId: ROUTE_IDS.ADMIN_FEE_RULES_DETAIL,
  readOperationId: OPERATION_IDS.FEE_RULE_READ,
};

const TAX_RULES_HOOKS: ResourceHookBinding = {
  rowIdField: 'id',
  routeParameterName: 'ruleId',
  paramName: 'ruleId',
  listRouteId: ROUTE_IDS.ADMIN_TAX_RULES_LIST,
  detailRouteId: ROUTE_IDS.ADMIN_TAX_RULES_DETAIL,
  readOperationId: OPERATION_IDS.TAX_RULE_READ,
};

export const RESOURCE_HOOK_REGISTRY: Record<string, ResourceHookBinding> = {
  instruments: INSTRUMENTS_HOOKS,
  'fact-batches': FACT_BATCHES_HOOKS,
  'corporate-actions': CORPORATE_ACTIONS_HOOKS,
  'reconciliation-runs': RECONCILIATION_RUNS_HOOKS,
  'import-jobs': IMPORT_JOBS_HOOKS,
  'clients': DATA_CLIENTS_HOOKS,
  'portfolios': DATA_PORTFOLIOS_HOOKS,
  'positions': DATA_POSITIONS_HOOKS,
  'journals': DATA_JOURNALS_HOOKS,
  'fiscal-periods': DATA_FISCAL_PERIODS_HOOKS,
  // Performance intentionally uses the backend-resolved generic resource
  // binding so the browser cannot substitute a realized-facts-only endpoint.
  'tax-records': DATA_TAX_RECORDS_HOOKS,
  'risk-exposures': DATA_RISK_EXPOSURES_HOOKS,
  'organization-users': ORGANIZATION_USERS_HOOKS,
  'organization-roles': ORGANIZATION_ROLES_HOOKS,
  'platform-organizations': PLATFORM_ORGANIZATIONS_HOOKS,
  'platform-users': PLATFORM_USERS_ROUTE_BINDING,
  'organization-branches': ORGANIZATION_BRANCHES_HOOKS,
  'organization-clients': ORGANIZATION_CLIENTS_HOOKS,
  'organization-client-accounts': ORGANIZATION_CLIENT_ACCOUNTS_HOOKS,
  'organization-portfolios': ORGANIZATION_PORTFOLIOS_HOOKS,
  'organization-audit': ORGANIZATION_AUDIT_HOOKS,
  'organization-fee-rules': FEE_RULES_HOOKS,
  'organization-tax-rules': TAX_RULES_HOOKS,
  'organization-module-activations': ORGANIZATION_MODULE_ACTIVATIONS_HOOKS,
};

export function getResourceHookBinding(resourceId: string): ResourceHookBinding | undefined {
  return RESOURCE_HOOK_REGISTRY[resourceId];
}
